import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { SERVICES } from "../data/content";
import { SITE } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-teal-600 focus:ring-2 focus:ring-teal-500/40";

export function RequestForm({ compact = false }) {
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const headcountParam = params.get("headcount");
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    service: params.get("service") || "",
    city: params.get("city") || "",
    preferred_time: "",
    urgency: "planned",
    notes: headcountParam ? `Estimated headcount: ${headcountParam} (from online ballpark estimator)` : "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/testing-requests`, form);
      toast.success("Request received — we will confirm your appointment shortly.", {
        description: `For anything urgent, call or text ${SITE.phoneDisplay} any time.`,
      });
      setForm({
        name: "", company: "", phone: "", email: "", service: "",
        city: "", preferred_time: "", urgency: "planned", notes: "",
      });
    } catch (err) {
      toast.error("Something went wrong sending your request.", {
        description: `Please call or text ${SITE.phoneDisplay} instead.`,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      data-testid="request-testing-form"
      onSubmit={submit}
      className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
    >
      <h3 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">
        Request Testing
      </h3>
      <p className="mt-2 text-sm text-slate-600">
        Planned requests only — for immediate needs, call or text {SITE.phoneDisplay}.
      </p>
      <div className={`mt-6 grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <input data-testid="request-form-name" required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} />
        <input data-testid="request-form-company" placeholder="Company / organization (optional)" value={form.company} onChange={set("company")} className={inputCls} />
        <input data-testid="request-form-phone" required type="tel" placeholder="Phone number *" value={form.phone} onChange={set("phone")} className={inputCls} />
        <input data-testid="request-form-email" required type="email" placeholder="Email address *" value={form.email} onChange={set("email")} className={inputCls} />
        <select data-testid="request-form-service" required value={form.service} onChange={set("service")} className={inputCls}>
          <option value="" disabled>Service required *</option>
          {SERVICES.map((s) => (
            <option key={s.id} value={s.title}>{s.title}</option>
          ))}
          <option value="Other / Not sure">Other / Not sure</option>
        </select>
        <input data-testid="request-form-city" required placeholder="Location / city in Iowa *" value={form.city} onChange={set("city")} className={inputCls} />
        <input data-testid="request-form-preferred-time" placeholder="Preferred date & time (optional)" value={form.preferred_time} onChange={set("preferred_time")} className={inputCls} />
        <select data-testid="request-form-urgency" value={form.urgency} onChange={set("urgency")} className={inputCls}>
          <option value="planned">Planned — schedule ahead</option>
          <option value="urgent">Urgent — as soon as possible</option>
        </select>
        <textarea
          data-testid="request-form-notes"
          placeholder="Additional notes (number of people, site details, anything helpful)"
          value={form.notes}
          onChange={set("notes")}
          rows={4}
          className={`${inputCls} sm:col-span-2 resize-none`}
        />
      </div>
      <button
        data-testid="request-testing-form-submit"
        type="submit"
        disabled={busy}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal-700 px-6 py-4 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-teal-800 disabled:opacity-60 sm:w-auto"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {busy ? "Sending…" : "Send Request"}
      </button>
    </form>
  );
}
